import {
	DialogBackdropState,
	FormattedText,
	Pagination,
	SearchInputForm,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	Typography
} from "client/Components";
import { contains } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useDeepComparison,
	useForceRerender,
	useSelection,
	useSortHandler
} from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	EventCategoryListItemModel,
	EventCategoryListModel,
	listCategories
} from "client/Api/EventsManagement/eventCategories";
import { HubManagementDialogContentLayout } from "../../HubManagementDialogContentLayout";
import { HubCategorySavedResult, useEditHubCategoryDialog } from "../../../EditHubCategoryDialog/EditHubCategoryDialog";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useDeleteHubItemCategoriesDialog } from "../../../DeleteHubItemCategoriesDialog";

export type EventsCategoriesProps = {
	onParentBackdropChange: (backdrop: DialogBackdropState) => void;
	isMobile: boolean;
	isResponsive: boolean;
	onCategoriesChanged: () => void;
};

export const EventsCategories = (props: EventsCategoriesProps): ReactElement => {
	const {
		onParentBackdropChange,
		isMobile,
		isResponsive,
		onCategoriesChanged
	} = props;

	const [categoryList, setCategoryList] = useState<EventCategoryListModel>({
		canCreateNewCategories: false,
		categories: []
	});

	const [selectedCategories, setSelectedCategories] = useState<readonly EventCategoryListItemModel[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [sortingState, setSortingState] =
		useState<DataSourceSortableState<EventCategoryListItemModel>>({
			direction: DataSourceSortDirection.Ascending,
			propertyName: "name"
		});

	const alertManager: AlertManager = useAlerts();
	const [reload, dataVersion] = useForceRerender();
	const [editCategoryDialog, openCategoryDialog] = useEditHubCategoryDialog();
	const [deleteHubItemCategoriesDialog, openDeleteHubItemCategoriesDialog] = useDeleteHubItemCategoriesDialog();
	const {
		sortingFor
	} = useSortHandler(sortingState, setSortingState);

	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm]);

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const {
		currentItems,
		pagination
	} = useEventCategoryDataSource(
		categoryList.categories,
		{
			searchTerm,
			pagination: {
				currentPageNumber
			},
			sorting: sortingState
		}
	);

	const editOrCreateCategoryHandler = useCallback(
		async (category?: EventCategoryListItemModel): Promise<void> => {
			onParentBackdropChange("invisible");

			const result: TryResult<HubCategorySavedResult> = await openCategoryDialog({
				kind: HubItemKind.Event,
				id: category?.id ?? null
			});

			onParentBackdropChange("visible");
			if (result.wasSuccessful) {
				reload();
				onCategoriesChanged();
			}
		},
		[
			onParentBackdropChange,
			openCategoryDialog,
			reload,
			onCategoriesChanged
		]
	);

	const deleteCategoriesHandler = useCallback(
		async (categories: readonly EventCategoryListItemModel[]): Promise<void> => {
			const result: TryResult<void> = await openDeleteHubItemCategoriesDialog({
				categoryIds: categories.map(x => x.id),
				hubItemKind: HubItemKind.Event
			});

			if (!result.wasSuccessful) {
				return;
			}

			setSelectedCategories([]);
			reload();
			onCategoriesChanged();
		},
		[
			openDeleteHubItemCategoriesDialog,
			reload,
			onCategoriesChanged
		]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);

					const categoryList: EventCategoryListModel = await listCategories(signal);
					setCategoryList(categoryList);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	const {
		selectionFor,
		multiSelection
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems: selectedCategories,
			setSelectedItems: setSelectedCategories
		}
	);

	const actionContext = useMemo(
		(): CategoryActionContext => {
			return {
				selectedCategories,
				canCreateNewCategories: categoryList.canCreateNewCategories,
				isMobile
			};
		},
		[
			categoryList.canCreateNewCategories,
			isMobile,
			selectedCategories
		]
	);

	const selectionActions = useMemo(
		(): readonly CategoryAction[] => {
			return getSelectionActions(
				() => setSelectedCategories([])
			);
		},
		[]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<CategoryActionContext> => {
			return getMainAction(
				editOrCreateCategoryHandler
			);
		},
		[editOrCreateCategoryHandler]
	);

	const contextualActions = useMemo(
		(): readonly CategoryAction[] => {
			return getContextualActions(
				editOrCreateCategoryHandler,
				deleteCategoriesHandler
			);
		},
		[
			deleteCategoriesHandler,
			editOrCreateCategoryHandler
		]
	);

	const hasSelection: boolean = selectedCategories.length > 0;

	const actions = useMemo(
		(): readonly CategoryAction[] => {
			return buildActionsForTopBar<CategoryActionContext, CategoryAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			mainAction,
			selectionActions,
			contextualActions,
			hasSelection,
			isResponsive
		]
	);

	const executeFor = (c: EventCategoryListItemModel): (() => void) | undefined => {
		const { canUpdateInformation, canUpdatePermissions } = c.permissions;
		return canUpdateInformation || canUpdatePermissions
			? () => editOrCreateCategoryHandler(c)
			: undefined;
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (categoryList.categories.length === 0) {
				return {
					icon: "the-hub",
					title: localize("_NoCategoriesYet"),
					message: localize("_ThereAreNoCategoriesYet")
				};
			}

			return {
				icon: "magnifying-glass",
				title: localize("_NoSearchResults"),
				message: localize("_NoCategoriesMatchedYourSearchKeywords")
			};
		},
		[
			categoryList.categories
				.length
		]
	);

	return (
		<HubManagementDialogContentLayout
			title={localize("_Categories")}
			isMobile={isMobile}
			isResponsive={isResponsive}
			actions={actions}
			actionContext={actionContext}
			hasSelection={hasSelection}
			trackingId="events-categories"
			isBusy={isBusy}
		>
			<HubManagementDialogContentLayout.Filter>
				<SearchInputForm
					onChange={setSearchTerm}
					trackingId="event-category-search-form"
					placeholder={
						<FormattedText
							text={localize("_FilterByProperties")}
							thingsToReplace={[
								{
									what: "{{Properties}}",
									filler: (
										<Typography
											weight="bold"
											isItalic
										>
											{localize("_Name")}
										</Typography>
									)
								}
							]}
						/>
					}
					height="large"
					icon="filter"
				/>
			</HubManagementDialogContentLayout.Filter>
			<HubManagementDialogContentLayout.Table>
				<TableView
					multiSelectable={multiSelection}
					items={currentItems}
					itemKey="id"
					height="stretch"
					columns={[
						{
							title: localize("_Name"),
							sortable: sortingFor("name"),
							width: 300,
							trackingId: "name"
						},
						{
							title: localize("_Description"),
							width: 300,
							trackingId: "description"
						},
						{
							title: localize("_PublishedItems"),
							width: 300,
							trackingId: "published-events-count"
						},
						{
							title: localize("_Drafts"),
							width: 300,
							trackingId: "draft-events-count"
						}
					]}
					trackingId="event-category-grid"
					noResultConfig={noResultConfig}
				>
					{c => (
						<TableView.Row
							selectable={selectionFor(c)}
							onExecute={executeFor(c)}
							trackingId={c.id.toString()}
						>
							<TableView.Cell>{c.name}</TableView.Cell>
							<TableView.Cell>{c.description}</TableView.Cell>
							<TableView.Cell>{c.publishedCount}</TableView.Cell>
							<TableView.Cell>{c.draftCount}</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="event-categories-pagination"
				/>
			</HubManagementDialogContentLayout.Table>
			{isResponsive && mainAction.canExecute(actionContext) && (
				<HubManagementDialogContentLayout.MainButton
					onClick={() => mainAction.execute(actionContext)}
					trackingId={mainAction.name}
				/>
			)}
			{editCategoryDialog}
			{deleteHubItemCategoriesDialog}
		</HubManagementDialogContentLayout>
	);
};
EventsCategories.displayName = "EventsCategories";

const useEventCategoryDataSource = createLocalDataSource<EventCategoryListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.name, searchTerm)
				|| contains(item.description, searchTerm)
		},
		hasPagination: true
	})
	.getHook();

type CategoryActionContext = {
	selectedCategories: readonly EventCategoryListItemModel[];
	canCreateNewCategories: boolean;
	isMobile: boolean;
};

type CategoryAction = ToolbarAction<CategoryActionContext>;

function getSelectionActions(
	deselectHandler: () => void
): readonly CategoryAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedCategories),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedCategories.length > 0,
			execute: () => deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedCategories.length > 0
		}
	];
}

function getMainAction(
	newCategoryHandler: () => void
): ToolbarRegularAction<CategoryActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNewCategory",
		title:  localize("_New"),
		canExecute: (c) => c.canCreateNewCategories,
		execute: () => newCategoryHandler(),
		icon: "plus",
		isPrimary: true
	};
}

function getContextualActions(
	editCategoryHandler: (item: EventCategoryListItemModel) => void,
	deleteCategoriesHandler: (item: readonly EventCategoryListItemModel[] ) => void
): readonly CategoryAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "editCategory",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedCategories.length === 1
				&& c.selectedCategories.every(c => c.permissions.canUpdateInformation || c.permissions.canUpdatePermissions),
			execute: (c) => editCategoryHandler(c.selectedCategories[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "deleteCategories",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedCategories.length > 0
				&& c.selectedCategories.every(c => c.permissions.canDelete),
			execute: (c) => deleteCategoriesHandler(c.selectedCategories),
			icon: "trash-can"
		}
	];
}